import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import {
    type NavigateFunction,
    type NavigateOptions,
    type To,
    useNavigate
} from "react-router-dom";

import { UnsavedChangesModal } from "./UnsavedChangesModal";

/**
 * Universal unsaved-changes guard.
 *
 * Components register their local dirty state via `useDirtyState`.
 * Programmatic navigations should be routed through
 * `useGuardedNavigate` (drop-in for `useNavigate`) so the guard can
 * intercept; non-navigation escape paths (in-page tab changes,
 * imperative actions) call `tryNavigate` from `useUnsavedChanges`
 * directly. Hard browser navigation (close tab, refresh, manual URL
 * change) is covered by a `beforeunload` listener — the browser will
 * show its own generic prompt.
 *
 * Discard flow: user confirms in the modal → the queued action fires
 * → the component navigates away → the dirty consumer unmounts → its
 * cleanup clears the dirty flag. No explicit "clear local state"
 * callback is needed since navigation unmounts the source of the
 * dirty state.
 *
 * Note: the guard does NOT block intra-component view switches that
 * preserve form state (e.g. EarnContent's Settings ↔ list toggle,
 * or the Distribution / Rewards Destination sub-tabs). Those keep
 * the data in component state across the switch, so there's nothing
 * to lose — the "Unsaved changes" pill in the header is the user's
 * cue that an Apply is still pending.
 */
interface UnsavedChangesContextValue {
    /** True when any consumer has registered itself as dirty. */
    isDirty: boolean;
    /** Run `action` immediately if clean, otherwise queue it and
     *  open the discard-confirm modal. The action fires only if the
     *  user confirms "Discard Changes". Fire-and-forget — no return. */
    tryNavigate: (action: () => void) => void;
    /** Promise variant of the guard. Resolves `true` if it's safe to
     *  proceed (clean OR user confirmed discard) and `false` if the
     *  user dismissed the modal. Useful for callsites that already
     *  return promises (async handlers, event-bus callbacks, shared
     *  components that accept an `onBefore*` prop). */
    canNavigate: () => Promise<boolean>;
    /** Imperatively set the dirty state. Consumers should prefer
     *  `useDirtyState` which handles the unmount cleanup; raw
     *  `setIsDirty` is exposed for advanced cases (e.g. a parent
     *  managing several dirty inputs via a single flag). */
    setIsDirty: (dirty: boolean) => void;
    /** Internal — `useDirtyState` plumbs the active discard handler
     *  through this so the modal's "Discard Changes" button can reset
     *  the consumer's local form state before the queued navigation
     *  fires. Not for direct consumer use; pass an `onDiscard`
     *  argument to `useDirtyState` instead. */
    _setOnDiscard: (handler: (() => void) | null) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
    null
);

export function UnsavedChangesProvider({
    children
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const [isDirty, setIsDirty] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    // Pending nav action lives in a ref so we can call it
    // synchronously inside the confirm handler without depending on
    // a render to thread the latest closure through. Reset to null
    // whenever the modal closes (confirmed or dismissed). The
    // promise variant uses a separate ref so both callstyles can
    // queue independently (rare but harmless).
    const pendingActionRef = useRef<(() => void) | null>(null);
    const pendingResolveRef = useRef<((ok: boolean) => void) | null>(null);
    // Active "reset to committed" handler from the currently-dirty
    // consumer. Stored in a ref (not state) so updates from the hook
    // don't trigger provider re-renders; the handler runs at the
    // moment the user confirms Discard.
    const onDiscardRef = useRef<(() => void) | null>(null);
    const setOnDiscard = useCallback((handler: (() => void) | null): void => {
        onDiscardRef.current = handler;
    }, []);

    const tryNavigate = useCallback(
        (action: () => void): void => {
            if (isDirty) {
                pendingActionRef.current = action;
                setModalOpen(true);
            } else {
                action();
            }
        },
        [isDirty]
    );

    const canNavigate = useCallback((): Promise<boolean> => {
        return new Promise((resolve) => {
            if (!isDirty) {
                resolve(true);
                return;
            }
            pendingResolveRef.current = resolve;
            setModalOpen(true);
        });
    }, [isDirty]);

    const handleDiscard = useCallback((): void => {
        const action = pendingActionRef.current;
        const resolve = pendingResolveRef.current;
        const onDiscard = onDiscardRef.current;
        pendingActionRef.current = null;
        pendingResolveRef.current = null;
        setModalOpen(false);
        // Reset the consumer's local form state to its committed
        // snapshot BEFORE firing the queued navigation. This is the
        // step that actually makes the changes "discarded" — for
        // in-component view toggles (e.g. EarnContent's Back) the
        // component stays mounted and would otherwise carry the
        // dirty state forward.
        onDiscard?.();
        // Clearing dirty here is cosmetic — once the discard handler
        // ran, the consumer's next render will see clean state and
        // re-register isDirty=false via useDirtyState — but it
        // avoids a one-frame flicker of the warning pill.
        setIsDirty(false);
        action?.();
        resolve?.(true);
    }, []);

    const handleOpenChange = useCallback((open: boolean): void => {
        if (open) return; // we never programmatically open via this path
        // User dismissed (X, Escape, backdrop click) → "keep editing".
        const resolve = pendingResolveRef.current;
        pendingActionRef.current = null;
        pendingResolveRef.current = null;
        setModalOpen(false);
        resolve?.(false);
    }, []);

    // Hard-nav guard. The browser's own confirm dialog kicks in for
    // close / refresh / manual URL change. We can't render a custom
    // modal here — the spec forbids it.
    useEffect(() => {
        if (!isDirty) return;
        const handler = (e: BeforeUnloadEvent): void => {
            e.preventDefault();
            // returnValue is required for Chrome to show the prompt;
            // the string is no longer surfaced by modern browsers.
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return (): void => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    return (
        <UnsavedChangesContext.Provider
            value={{
                isDirty,
                tryNavigate,
                canNavigate,
                setIsDirty,
                _setOnDiscard: setOnDiscard
            }}
        >
            {children}
            <UnsavedChangesModal
                open={modalOpen}
                onOpenChange={handleOpenChange}
                onDiscard={handleDiscard}
            />
        </UnsavedChangesContext.Provider>
    );
}

/**
 * Safe default when no `<UnsavedChangesProvider>` is mounted — used by
 * external (sim-only) consumers that embed a widget like `TradeWidget`
 * outside the app shell. There's no navigation to guard in that context,
 * so `tryNavigate` runs the action immediately and dirty-tracking is a
 * no-op. apps/main always mounts the provider, so it gets the real API.
 */
const NO_PROVIDER_FALLBACK: UnsavedChangesContextValue = {
    isDirty: false,
    tryNavigate: (action) => action(),
    canNavigate: () => Promise.resolve(true),
    setIsDirty: () => {},
    _setOnDiscard: () => {}
};

/**
 * Read the guard's API. Outside a provider it returns a safe no-op
 * fallback (see above) rather than throwing, so the shared widgets can
 * be embedded standalone without pulling in the app-shell provider.
 */
export function useUnsavedChanges(): UnsavedChangesContextValue {
    return useContext(UnsavedChangesContext) ?? NO_PROVIDER_FALLBACK;
}

/**
 * Register the calling component's dirty state with the provider.
 * Whenever `dirty` is true the global guard arms; on unmount the
 * registration clears automatically so a navigated-away component
 * never leaves the warning behind.
 *
 * `onDiscard` (optional) fires when the user confirms "Discard
 * Changes" in the modal — use it to reset the consumer's local form
 * state to its committed snapshot. Required for in-component
 * navigation (e.g. a Back button that toggles a view but doesn't
 * unmount the form) since without it the local state outlives the
 * "discard" and the form re-arms as dirty on the next render.
 *
 * Single-source assumption: at most one consumer registers dirty
 * state at a time. If we ever need multiple concurrent forms with
 * dirty state, refactor this to a Set-keyed registry.
 */
export function useDirtyState(dirty: boolean, onDiscard?: () => void): void {
    const { setIsDirty, _setOnDiscard } = useUnsavedChanges();
    // Stash the latest discard handler so the registration effect
    // doesn't need to re-run on every render. We always read
    // `onDiscardRef.current` from the registered wrapper, so the
    // most-recent closure (capturing current committed state) fires
    // when the user confirms.
    const onDiscardRef = useRef(onDiscard);
    onDiscardRef.current = onDiscard;

    useEffect(() => {
        setIsDirty(dirty);
        _setOnDiscard(
            dirty ? (): void | undefined => onDiscardRef.current?.() : null
        );
        return (): void => {
            setIsDirty(false);
            _setOnDiscard(null);
        };
    }, [dirty, setIsDirty, _setOnDiscard]);
}

/**
 * Drop-in for `useNavigate` that routes every navigation through
 * the guard. Use this in any component that might trigger
 * cross-page navigation while a dirty form is in scope, such as the
 * NavigationBar or page-level CTA buttons. For raw
 * `useNavigate` consumers that never navigate while dirty (deep
 * internal routes that always happen after a successful save), the
 * unguarded version is still fine.
 */
export function useGuardedNavigate(): NavigateFunction {
    const navigate = useNavigate();
    const { tryNavigate } = useUnsavedChanges();
    const guarded = useCallback(
        (to: To | number, options?: NavigateOptions): void => {
            tryNavigate(() => {
                if (typeof to === "number") {
                    navigate(to);
                } else {
                    navigate(to, options);
                }
            });
        },
        [navigate, tryNavigate]
    );
    return guarded as NavigateFunction;
}
